import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const statsData = [
    { id: 1, endValue: 160, suffix: '+', text: 'Engaged with 163 startups in last 10 years' },
    { id: 2, endValue: 28, suffix: '+', text: 'Helped portfolio startups raise 28M+ USD' },
    { id: 3, endValue: 84, suffix: '%', text: 'Startups achieving growth milestones' },
];

const SuccessStatsSection = () => {
    const sectionRef = useRef(null);
    const containerRef = useRef(null);
    const titleCardRef = useRef(null);
    const statCardsRefs = useRef([]);
    const numberRefs = useRef([]);

    useEffect(() => {
        // Clear refs on re-render to avoid duplicates
        statCardsRefs.current = statCardsRefs.current.slice(0, statsData.length);
        numberRefs.current = numberRefs.current.slice(0, statsData.length);

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 80%", // Triggers when top of section hits 80% of viewport
                    toggleActions: "play none none none",
                }
            });

            // 1. Container Pop
            tl.from(containerRef.current, {
                scale: 0.9,
                opacity: 0,
                duration: 0.8,
                ease: "back.out(1.7)",
            });

            // 2. Title Card Slide In
            tl.from(titleCardRef.current, {
                x: -50,
                opacity: 0,
                duration: 0.6,
                ease: "power2.out"
            }, "-=0.5");

            // 3. Stat Cards Slide Up
            tl.from(statCardsRefs.current, {
                y: 50,
                opacity: 0,
                duration: 0.6,
                stagger: 0.2,
                ease: "power2.out"
            }, "-=0.4");

            // 4. Numbers Counter
            statsData.forEach((stat, index) => {
                const numRef = numberRefs.current[index];
                if (!numRef) return;
                
                let proxy = { val: 0 };
                gsap.to(proxy, {
                    val: stat.endValue,
                    duration: 2,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: "top 80%",
                    },
                    onUpdate: () => {
                        numRef.innerText = Math.ceil(proxy.val) + stat.suffix;
                    }
                });
            });
        }, sectionRef); // Scope GSAP to this component

        return () => ctx.revert(); // Cleanup GSAP on unmount
    }, []);

    return (
        <section ref={sectionRef} className='container max-w-7xl mx-auto px-6 md:px-12 mt-24 mb-12'>
            {/* Replaced bg-[#182589] with bg-blue-900 and fixed grid breakpoints */}
            <ol
                ref={containerRef}
                className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 bg-blue-900 rounded-3xl p-5 gap-4 overflow-hidden shadow-xl'
            >
                {/* Title Card */}
                <li ref={titleCardRef} className='h-full'>
                    {/* Replaced bg-[#03C0F2] with bg-cyan-500 */}
                    <article className='flex flex-col justify-center px-6 py-10 rounded-3xl bg-cyan-500 h-full text-white'>
                        <h4 className='font-bold text-3xl mb-2 leading-tight'>Success Stories</h4>
                        <p className='font-medium text-xl opacity-90'>Real Startups, Real Impact</p>
                    </article>
                </li>

                {/* Stat Cards */}
                {statsData.map((stat, index) => (
                    <li 
                        key={stat.id} 
                        ref={(el) => (statCardsRefs.current[index] = el)}
                        className='h-full'
                    >
                        <article className='flex flex-col p-8 rounded-3xl bg-white h-full justify-center items-center shadow-sm'>
                            <h4
                                ref={(el) => (numberRefs.current[index] = el)}
                                className='font-medium text-4xl text-blue-900 mb-2'
                            >
                                0{stat.suffix}
                            </h4>
                            <p className='font-medium text-base text-center text-gray-800 leading-snug'>
                                {stat.text}
                            </p>
                        </article>
                    </li>
                ))}
            </ol>
        </section>
    );
};

export default SuccessStatsSection;